import React from "react";
//#region types
import { WintSuperkollAPIModel as ApiModels } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { DateConstants, RouteConstants, SettingsConstants } from "utils/constants";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { ActionLinkButton, Icon, RoundedIcon, useCheckRelations, useMountedState } from "components/common";
import WidgetSkeleton from "components/common/skeletons/widgetSkeleton";
import { useLocation, useNavigate } from "react-router-dom";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import { useBankBalanceService } from "serviceHooks";
import { IconDefinitionConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
import clsx from "clsx";

/**
 * Current bank balance (real UB) per bank account with a live ISO 20022
 * connection, with the date each balance refers to.
 */
const BankBalanceWidget: React.FC = () => {
  const bankBalanceService = useBankBalanceService();
  const { getMounted } = useMountedState();
  const [balances, setBalances] = React.useState<ApiModels.BankBalance[]>();
  const [loadFailed, setLoadFailed] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Matches settings.tsx's own gate — no point linking to a section the user can't see.
  const { getIsAdmin, getIsAuditor } = useCheckRelations();
  const canManageBankBalanceSettings = getIsAdmin() && !getIsAuditor();

  const onSettingsClick = () => {
    navigate(
      UrlHelper.formatPath(
        `/Settings?${RouteConstants.QueryParamStrings.Section}=${SettingsConstants.NavigationKey.BankBalances}`
      ),
      { state: { background: location } }
    );
  };

  React.useEffect(() => {
    bankBalanceService
      .getDashboardBalances()
      .then((res) => {
        if (getMounted()) {
          setBalances(res ?? []);
        }
      })
      // The service doesn't toast, so without this the skeleton would sit forever.
      .catch(() => {
        if (getMounted()) {
          setBalances([]);
          setLoadFailed(true);
        }
      });
  }, []);

  // A null balance renders as a dash rather than zero: "0 kr" is a plausible wrong
  // figure on a money widget, which is worse than visibly having no figure.
  const formatAmount = (balance: ApiModels.BankBalance) =>
    balance.balance === undefined || balance.balance === null
      ? "–"
      : LocalizationHelperInstance.getFormattedCurrency(balance.balance, balance.currency, 0, 0);

  const balanceCaption = (balance: ApiModels.BankBalance) => {
    if (!balance.balanceDate) {
      return undefined;
    }

    const asOf = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.BankBalanceAsOf, undefined, {
      date: LocalizationHelperInstance.getFormattedDate(balance.balanceDate, DateConstants.DateFullMonthConfig, true),
    });

    return balance.isStale
      ? `${asOf} · ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.BankBalanceStale)}`
      : asOf;
  };

  // More than half the companies with several bank accounts hold two of them in the same
  // bank, and two rows both reading "Handelsbanken" against different amounts cannot be
  // told apart. Only those rows fall back to carrying the bookkeeping account number.
  const ambiguousBankNames = React.useMemo(() => {
    const countByName = new Map<string, number>();

    (balances ?? []).forEach((balance) => {
      if (balance.bankName) {
        countByName.set(balance.bankName, (countByName.get(balance.bankName) ?? 0) + 1);
      }
    });

    return new Set(
      Array.from(countByName.entries())
        .filter(([, count]) => count > 1)
        .map(([name]) => name)
    );
  }, [balances]);

  const accountLabel = (balance: ApiModels.BankBalance) =>
    balance.bankName && !ambiguousBankNames.has(balance.bankName)
      ? balance.bankName
      : [balance.accountNumber, balance.bankName].filter(Boolean).join(" · ");

  if (!balances) {
    return <WidgetSkeleton />;
  }

  if (balances.length === 0) {
    if (loadFailed) {
      return (
        <Grid container direction="column" wrap="nowrap" className={cssExports.bankBalanceWidgetContainer}>
          <Grid item container direction="row" wrap="nowrap" alignItems="center" sx={{ gap: 2 }}>
            <Grid item>
              <RoundedIcon icon={IconDefinitionConstants.WBank} displayType={StyleEnums.DisplayType.None} />
            </Grid>
            <Grid item xs container direction="column" wrap="nowrap">
              <Grid item className={cssExports.emptyStateTitle}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.BankBalanceCouldNotLoadTitle)}
              </Grid>
              <Grid item className={cssExports.emptyStateDescription}>
                {LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Common.BankBalanceCouldNotLoadDescription
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container direction="column" wrap="nowrap" className={cssExports.bankBalanceWidgetContainer}>
        <Grid item container direction="row" wrap="nowrap" alignItems="flex-start" sx={{ gap: 2 }}>
          <Grid item>
            <RoundedIcon icon={IconDefinitionConstants.WBank} displayType={StyleEnums.DisplayType.None} />
          </Grid>
          <Grid item xs container direction="column" wrap="nowrap">
            <Grid item className={cssExports.emptyStateTitle}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.BankBalanceNothingToShowTitle)}
            </Grid>
            <Grid item container direction="row" wrap="wrap" alignItems="center" columnGap={1}>
              <Grid item className={cssExports.emptyStateDescription}>
                {LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Common.BankBalanceNothingToShowDescription
                )}
              </Grid>
              {canManageBankBalanceSettings && (
                <Grid item>
                  <ActionLinkButton
                    className={clsx(cssExports.seeMoreLink, cssExports.animatedLink)}
                    onClick={onSettingsClick}
                    endIcon={
                      <Icon icon={IconDefinitionConstants.WArrowSmallRight} displayType={StyleEnums.DisplayType.Info} />
                    }
                  >
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Settings)}
                  </ActionLinkButton>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  const leadBalance = balances.find((balance) => balance.isHighlighted) ?? balances[0];
  const remainingBalances = balances.filter((balance) => balance !== leadBalance);

  return (
    <Grid container className={cssExports.bankBalanceWidgetContainer} direction="column" wrap="nowrap">
      <Grid
        item
        container
        direction="row"
        wrap="nowrap"
        alignItems="center"
        className={cssExports.mainRow}
        sx={{ gap: 2 }}
      >
        <Grid item>
          <RoundedIcon icon={IconDefinitionConstants.WBank} displayType={StyleEnums.DisplayType.Info} />
        </Grid>
        <Grid item xs sx={{ minWidth: 0 }} container direction="column" wrap="nowrap">
          <Grid item className={cssExports.mainAmount}>
            {formatAmount(leadBalance)}
          </Grid>
          <Grid item container direction="row" wrap="nowrap" sx={{ gap: 1 }}>
            <Grid item className={cssExports.accountLabel}>
              {accountLabel(leadBalance)}
            </Grid>
            <Grid item className={cssExports.rowDate}>
              {balanceCaption(leadBalance)}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {remainingBalances.map((balance) => (
        <Grid
          key={balance.accountId}
          item
          container
          direction="row"
          wrap="nowrap"
          className={cssExports.compactRow}
          sx={{ gap: 1 }}
        >
          <Grid item xs sx={{ minWidth: 0 }}>
            <Grid container direction="column" wrap="nowrap">
              <Grid item className={cssExports.accountLabel}>
                {accountLabel(balance)}
              </Grid>
              <Grid item className={cssExports.rowDate}>
                {balanceCaption(balance)}
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={cssExports.rowAmount}>
            {formatAmount(balance)}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};

export default BankBalanceWidget;
